import React from 'react';
import styled from 'styled-components';
import { buildListingFullAddress } from 'utils';
import { ListingController } from 'api';
import {
  SearchType,
  FormattedAnsweringListingForAppointmentDeskType,
  ColumnOptions,
} from './types';
import Contact from '../Contact';
import Thumbnail from '../Thumbnail';
import assume from './assume';
import { BrokerageInfo, ListingInfo, SubscriptionTag } from './components';
import AnsweringListingActionButtons from './components/AnsweringListingActionButtons';
import { availabilityOrder } from './constants';

const appointmentDeskCrossOrgListingSearch = (
  options: ColumnOptions,
  setActiveRow: React.Dispatch<React.SetStateAction<null | number>>,
): SearchType<FormattedAnsweringListingForAppointmentDeskType> => ({
  columns: [
    {
      title: '',
      dataIndex: 'thumbnail',
      key: 'thumbnail',
      className: 'eye-column cross-org',
      width: 85,
      render: (
        thumbnail: FormattedAnsweringListingForAppointmentDeskType['thumbnail'],
      ) => <Thumbnail src={thumbnail} />,
    },
    {
      title: '',
      key: 'address',
      className: 'address-column cross-org',
      render: (
        _,
        {
          line1,
          line2,
          province,
          city,
          availability,
          price,
          status,
          dom,
          mlsTypes,
        },
      ) => (
        <ListingInfo
          line1={line1}
          line2={line2}
          province={province}
          city={city}
          availability={availability}
          price={price}
          status={status}
          dom={dom}
          mlsTypes={mlsTypes}
        />
      ),
    },
    {
      title: '',
      key: 'org',
      className: 'org-column',
      render: (_, { org }) => (
        <BrokerageInfo
          orgName={org.name}
          orgLogoUrl={org.logoUrl}
          dataCy={`listing-brokerage-${org.id}`}
        />
      ),
    },
    {
      title: '',
      className: 'appointment-desk-agent-column agents-column',
      dataIndex: 'reps',
      key: 'reps',
      render: (
        agents: FormattedAnsweringListingForAppointmentDeskType['reps'],
        { subscriptionTag },
      ) => {
        if (agents.length === 0) {
          return null;
        }
        return (
          <>
            {agents.map(agent => (
              <div
                key={agent.id}
                onClick={e => e.stopPropagation()}
                data-cy={`listing-agent-${agent.id}`}
              >
                <Contact
                  avatarSize={24}
                  contact={agent}
                  underlineOnHover
                  viewNameOnToolTip
                  subscriptionTag={subscriptionTag}
                />
              </div>
            ))}
          </>
        );
      },
    },
    {
      title: 'Subscription Tag',
      dataIndex: 'subscriptionTag',
      key: 'subscriptionTag',
      width: 50,
      className: 'cross-org',
      render: (
        subscriptionTag: FormattedAnsweringListingForAppointmentDeskType['subscriptionTag'],
      ) => (
        <SubscriptionTag
          subscriptionTag={subscriptionTag}
          toolTipTextType="listing"
        />
      ),
    },
    {
      title: '',
      dataIndex: 'id',
      key: 'button',
      className: 'cross-org',
      render: (
        id: string,
        { auth, org, forceBlur, reps, subscriptionTag, preBookingInformation },
        index,
      ) => (
        <AnsweringListingActionButtons
          id={id}
          org={org}
          forceBlur={forceBlur}
          reps={reps}
          auth={auth}
          index={index}
          subscriptionTag={subscriptionTag}
          preBookingInformation={preBookingInformation}
        />
      ),
    },
  ],
  search: async (
    debouncedQuery,
    forceBlur,
    { auth, mlsOptionsCount, mlsFilter },
  ) => {
    let mlsTypesFilter: string[] = [];
    if (mlsFilter && mlsFilter.length !== mlsOptionsCount)
      mlsTypesFilter = mlsFilter;

    const { listings } = await ListingController.findAnsweringListings({
      search: debouncedQuery,
      ...(mlsTypesFilter.length ? { mlsTypes: mlsTypesFilter } : {}),
    });

    const data = listings.map(listing => ({
      id: listing.id,
      line1: listing?.line1,
      line2: listing?.line2,
      dom: listing?.dom,
      province: listing.province,
      city: listing.city,
      price: listing.list_price,
      fullAddress: `${buildListingFullAddress(listing.line1, listing.line2)}, ${
        listing.city
      }, ${listing.province}, ${listing.postal_code}`,
      fullReps: listing.fullReps,
      list_price: listing.list_price,
      type: listing.type,
      status: listing.status,
      mls_numbers: listing.mls_numbers,
      availability: listing.availability,
      location: listing.location,
      reps: listing.reps,
      org: listing.org,
      subscriptionTag: listing.subscriptionTag,
      thumbnail: listing.thumbnail,
      mlsTypes: listing.mlsTypes,
      preBookingInformation: listing.preBookingInformation,
    }));

    const { length } = data;
    return data
      .map(listing => {
        const {
          id,
          line1,
          line2,
          city,
          province,
          dom,
          fullAddress,
          fullReps,
          list_price: price,
          type,
          status,
          mls_numbers,
          availability,
          reps,
          org,
          thumbnail,
          subscriptionTag,
          mlsTypes,
          preBookingInformation,
        } = listing;
        const availabilityLabel = availability.replace(/_/g, ' ') as any;
        return {
          id,
          key: id,
          availability,
          reps,
          city,
          province,
          line1,
          line2,
          fullAddress,
          fullReps,
          price,
          type,
          dom,
          status: {
            type: status,
            mlsNumbers: mls_numbers,
          },
          thumbnail,
          org,
          auth,
          mlsTypes,
          subscriptionTag,
          preBookingInformation,
          forceBlur,
          value:
            (availabilityOrder.indexOf(availabilityLabel) + length + 1) %
            (length + 1),
        };
      })
      .sort((a, b) => a.value - b.value);
  },
  onConfirm: (itemSelected, { auth, updateState }) => {
    const { id, orgId } = itemSelected;
    const redirect = `/listing/${id}/appointments/book`;
    if (auth.isAssumedNotBB) {
      assume(orgId, true, redirect);
    } else {
      updateState({ redirect });
    }
  },
  createOnRow:
    ({ auth, history, forceBlur, answeringConfirm }) =>
    (item, index) => ({
      onClick: answeringConfirm(
        { type: 'listing', subscriptionTag: item.subscriptionTag },
        () => {
          forceBlur();
          if (
            item.org.id !== auth.org.id &&
            (auth.assumerIdMlsStaff || auth.isAssumedNotBB)
          ) {
            assume(
              item.org.id,
              Boolean(auth.isAssumedNotBB),
              `/listing/${item.id}/view`,
            );
          } else {
            history.push(`/listing/${item.id}/view`);
          }
        },
      ),
      onMouseMove: () => setActiveRow(index),
      onMouseLeave: () => setActiveRow(null),
      'data-cy': `universal-listing-item-${item.id}`,
    }),
});

export { appointmentDeskCrossOrgListingSearch };

const StyledAvatarGroupContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  grid-gap: 4px;
`;
